/**
 * workspaceSettings.ts — the workspace's currency + date/number format row
 * (migration 0153, table `workspace_settings`, one row per workspace).
 *
 * WHY A NEW TABLE AND NOT workspaces.currency: the workspaces row is only
 * updatable by super_admin (workspaces_update RLS), while Lis's rule for CRM
 * configuration is crm.manage. The migration seeds this table FROM
 * workspaces.currency, so day one nothing changes; from then on this row is
 * the source of truth.
 *
 * HOW IT REACHES THE APP. ModuleContext loads this row alongside the company
 * profile and (a) overlays `currency` onto the company object — so
 * useWorkspaceCurrency() and every `company?.currency` call site (Invoices,
 * Quotes, AR-aging…) pick it up with zero further changes — and (b) pushes the
 * date/number formats into lib/format.ts via setFormatPreferences(), so
 * formatDate/formatMoney render in the chosen style app-wide.
 *
 * FAIL-OPEN: before 0153 is applied the table doesn't exist; reads return null
 * and everything behaves exactly as today (workspaces.currency + fixed
 * European formats).
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from './supabase';
import { requireWorkspaceId } from './apiClient';
import type { DateFormatKey, NumberFormatKey } from './format';

export interface WorkspaceSettings {
  workspace_id: string;
  currency: string;
  date_format: DateFormatKey;
  number_format: NumberFormatKey;
}

export const workspaceSettingsKey = ['workspace_settings'] as const;

export const CURRENCY_OPTIONS = [
  'EUR', 'USD', 'GBP', 'CHF', 'CAD', 'AUD', 'JPY', 'SEK', 'NOK', 'DKK', 'PLN',
] as const;

export const DATE_FORMAT_OPTIONS: { value: DateFormatKey; label: string }[] = [
  { value: 'DD/MM/YYYY', label: '17/06/2026 (European)' },
  { value: 'MM/DD/YYYY', label: '06/17/2026 (US)' },
  { value: 'YYYY-MM-DD', label: '2026-06-17 (ISO)' },
];

export const NUMBER_FORMAT_OPTIONS: { value: NumberFormatKey; label: string }[] = [
  { value: '1.234,56', label: '1.234,56 (point grouping)' },
  { value: '1,234.56', label: '1,234.56 (comma grouping)' },
  { value: '1 234,56', label: '1 234,56 (space grouping)' },
];

const SETTINGS_SELECT = 'workspace_id, currency, date_format, number_format';

/** Plain fetch — used by ModuleContext at load time. Null when the table is
 *  missing (pre-0153) or no row exists yet; callers treat null as "today's
 *  defaults" and change nothing. */
export async function fetchWorkspaceSettings(workspaceId: string): Promise<WorkspaceSettings | null> {
  try {
    const { data, error } = await supabase
      .from('workspace_settings')
      .select(SETTINGS_SELECT)
      .eq('workspace_id', workspaceId)
      .maybeSingle();
    if (error) return null;
    return (data as WorkspaceSettings | null) ?? null;
  } catch {
    return null;
  }
}

/** The editor's read. `available: false` = migration not applied yet. */
export function useWorkspaceSettings() {
  const query = useQuery({
    queryKey: workspaceSettingsKey,
    retry: false,
    queryFn: async (): Promise<WorkspaceSettings | null> => {
      const workspaceId = requireWorkspaceId();
      return fetchWorkspaceSettings(workspaceId);
    },
  });
  return { ...query, settings: query.data ?? null };
}

export interface SaveResult { ok: boolean; error?: string }

/** Upsert the one row. RLS: crm.manage required (enforced in Postgres). */
export async function saveWorkspaceSettings(
  patch: Partial<Omit<WorkspaceSettings, 'workspace_id'>>,
): Promise<SaveResult> {
  try {
    const workspace_id = requireWorkspaceId();
    const { error } = await supabase
      .from('workspace_settings')
      .upsert(
        { workspace_id, ...patch, updated_at: new Date().toISOString() },
        { onConflict: 'workspace_id' },
      );
    if (error) throw error;
    return { ok: true };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}
